import { endOfDay, formatDate, isNil, isSameDay, startOfDay } from "lopermut"
import { Check, ChevronLeft, ListFilter, Plus, Search, X } from "lucide-react"
import {
  KeyboardEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { DateRange } from "react-day-picker"
import { Button } from "../atoms/Button"
import { Calendar } from "../atoms/Calendar"
import { Checkbox } from "../atoms/Checkbox"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../atoms/Popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../atoms/Tooltip"
import { cn } from "../utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./Command"

const MAX_LABELS_IN_SUMMARY = 2
const MAX_FIELD_SUGGESTIONS = 5
const OVERFLOW_PILL_WIDTH = 52
const MAX_VALUE_SUGGESTIONS = 8

type FrozenAnchor = {
  contextElement: Element
  getBoundingClientRect: () => DOMRect
}

export type FilterBarOption = {
  value: string
  label: string
  group?: string
  className?: string
}

type BaseFilterBarField = {
  key: string
  label: string
  condition?: boolean
}

type BaseOptionsFilterBarField = BaseFilterBarField & {
  options: readonly FilterBarOption[]
  optionsSearch?: string
  onOptionsSearchChange?: (query: string) => void
}

export type MultiSelectFilterBarField = BaseOptionsFilterBarField & {
  type: "multiSelect"
  value: string[] | undefined
  onChange: (value: string[] | undefined) => void
}

export type SelectFilterBarField = BaseOptionsFilterBarField & {
  type: "select"
  value: string | undefined
  onChange: (value: string | undefined) => void
}

export type DateRangeFilterBarField = BaseFilterBarField & {
  type: "dateRange"
  value: DateRange | undefined
  onChange: (value: DateRange | undefined) => void
}

export type FilterBarField =
  MultiSelectFilterBarField | SelectFilterBarField | DateRangeFilterBarField

export type FilterBarLabels = {
  addFilter: string
  clear: string
  clearFilters: string
  removeFilter: string
  search: string
  searchFor: (query: string) => string
  empty: string
  selectedCount: (count: number) => string
  any: string
}

export type FilterBarProps = {
  className?: string
  fields: FilterBarField[]
  labels: FilterBarLabels
  onClearFilters: () => void
  searchQuery?: string
  onSearchQueryChange: (query: string) => void
  searchPlaceholder?: string
  trailing?: ReactNode
}

export function FilterBar(props: FilterBarProps) {
  const { className, fields, labels, onClearFilters, trailing, onSearchQueryChange } = props
  const [openKey, setOpenKey] = useState<string>()
  const [editingKey, setEditingKey] = useState<string>()
  const [draft, setDraft] = useState(props.searchQuery ?? "")
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [markedKey, setMarkedKey] = useState<string>()
  const boxRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const frozenLeftRef = useRef<number>(undefined)
  const anchorRef = useRef({
    getBoundingClientRect: () => {
      const rect = searchRef.current?.getBoundingClientRect() ?? new DOMRect()
      return new DOMRect(frozenLeftRef.current ?? rect.left, rect.top, rect.width, rect.height)
    },
  })

  const visibleFields = fields.filter((field) => field.condition !== false)
  const activeKeys = visibleFields.filter(isFieldActive).map((field) => field.key)
  const [keyOrder, setKeyOrder] = useState(activeKeys)
  const orderedKeys = mergeKeyOrder(keyOrder, activeKeys)
  if (orderedKeys.join() !== keyOrder.join()) setKeyOrder(orderedKeys)

  const pillFields = orderedKeys.flatMap(
    (key) => visibleFields.find((field) => field.key === key) ?? [],
  )
  const addableFields = visibleFields.filter((field) => !pillFields.includes(field))
  const editingField = visibleFields.find((field) => field.key === editingKey)
  const suggestions = getSuggestions(visibleFields, addableFields, draft)
  const isSuggestionsOpen = isSuggesting && (!isNil(editingField) || suggestions.length > 0)

  useEffect(() => {
    suggestionsRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  useLayoutEffect(() => {
    frozenLeftRef.current = isSuggestionsOpen
      ? searchRef.current?.getBoundingClientRect().left
      : undefined
  }, [isSuggestionsOpen])

  function onPillOpenChange(field: FilterBarField, open: boolean) {
    setOpenKey(open ? field.key : undefined)
  }

  function onRemoveField(field: FilterBarField) {
    field.onChange(undefined)
  }

  function onClearAll() {
    onClearFilters()
    setDraft("")
  }

  function showSuggestions() {
    setEditingKey(undefined)
    setIsSuggesting(true)
  }

  function onDraftChange(text: string) {
    const typedField = findTypedField(visibleFields, text)

    if (typedField) {
      addTypedValues(typedField, text.slice(text.indexOf(":") + 1))
      openField(typedField)
      return
    }

    setDraft(text)
    setActiveIndex(text.trim() ? 0 : -1)
    setMarkedKey(undefined)
    showSuggestions()
  }

  function openField(field: FilterBarField) {
    setDraft("")
    commitSearch("")
    setEditingKey(field.key)
    setIsSuggesting(true)
  }

  function commitSearch(text: string) {
    const query = text.trim()
    if (query === (props.searchQuery ?? "")) return

    onSearchQueryChange(query)
  }

  function onApplySuggestion(suggestion: Suggestion) {
    switch (suggestion.kind) {
      case "search":
        commitSearch(draft)
        setIsSuggesting(false)
        return
      case "field":
        openField(suggestion.field)
        return
      case "value":
        addValues(suggestion.field, [suggestion.option.value])
        setDraft("")
        setIsSuggesting(false)
        commitSearch("")
        return
    }
  }

  function onBoxKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const box = event.currentTarget
    const target = event.target
    if (!(target instanceof HTMLElement) || !box.contains(target)) return

    if (event.key === "Delete" || event.key === "Backspace") {
      const pillKey = target.closest<HTMLElement>("[data-filter-key]")?.dataset.filterKey
      const field = pillFields.find((pillField) => pillField.key === pillKey)
      if (!field) return

      event.preventDefault()
      onRemoveField(field)
      searchRef.current?.focus()
      return
    }

    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    if (target instanceof HTMLInputElement && !isCaretAtEdge(target, event.key)) return

    const items = [...box.querySelectorAll<HTMLElement>("button, input")]
    const next = items[items.indexOf(target) + (event.key === "ArrowLeft" ? -1 : 1)]
    if (!next) return

    event.preventDefault()
    next.focus()
  }

  function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        showSuggestions()
        setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1))
        return
      case "ArrowUp":
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      case "Enter": {
        const suggestion = suggestions[activeIndex]
        event.preventDefault()

        if (isSuggestionsOpen && suggestion) {
          onApplySuggestion(suggestion)
        } else {
          commitSearch(draft)
        }

        return
      }
      case "Escape":
        commitSearch(draft)
        setIsSuggesting(false)
        return
      case "Backspace": {
        const lastField = pillFields.at(-1)
        if (draft !== "" || !lastField) return

        if (markedKey === lastField.key) {
          onRemoveField(lastField)
          setMarkedKey(undefined)
        } else {
          setMarkedKey(lastField.key)
        }
      }
    }
  }

  return (
    <div className={cn("flex flex-row gap-2 items-start", className)}>
      <Popover open={isSuggestionsOpen} onOpenChange={setIsSuggesting}>
        <PopoverAnchor virtualRef={anchorRef} />
        <div
          ref={boxRef}
          className={cn(
            "flex-1 min-w-0 flex flex-nowrap items-center gap-1.5 min-h-10 pl-2 pr-1 py-1 rounded-md border border-input bg-background text-sm ring-offset-background",
            "has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-ring has-[input:focus-visible]:ring-offset-2",
          )}
          onClick={(event) => {
            if (event.target === event.currentTarget) searchRef.current?.focus()
          }}
          onKeyDown={onBoxKeyDown}
        >
          <Search className="h-4 w-4 mx-1 shrink-0 text-muted-foreground" />
          <InlinePills
            fields={pillFields}
            labels={labels}
            boxRef={boxRef}
            onRemove={onRemoveField}
            renderPill={(field) => (
              <FilterPill
                key={field.key}
                field={field}
                labels={labels}
                isOpen={openKey === field.key}
                isMarked={markedKey === field.key}
                onOpenChange={(open) => onPillOpenChange(field, open)}
                onRemove={() => onRemoveField(field)}
              />
            )}
          />
          <input
            ref={searchRef}
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={onSearchKeyDown}
            onFocus={() => {
              setActiveIndex(-1)
              showSuggestions()
            }}
            onBlur={(event) => {
              if (suggestionsRef.current?.contains(event.relatedTarget)) return

              commitSearch(draft)
              setIsSuggesting(false)
              setMarkedKey(undefined)
            }}
            placeholder={props.searchPlaceholder}
            className="flex-1 min-w-40 md:min-w-60 h-7 bg-transparent outline-none placeholder:text-muted-foreground"
          />
          <div className="flex items-center shrink-0">
            <AddFilterButton fields={visibleFields} addableFields={addableFields} labels={labels} />
            {(pillFields.length > 0 || draft !== "" || !isNil(props.searchQuery)) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7.5 w-7.5 shrink-0 rounded-sm"
                      aria-label={labels.clearFilters}
                      onClick={onClearAll}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{labels.clearFilters}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        <PopoverContent
          ref={suggestionsRef}
          className={editingField ? "p-0 w-auto min-w-56" : "p-1 w-80 max-h-80 overflow-y-auto"}
          align="start"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (boxRef.current?.contains(event.target as Node)) event.preventDefault()
          }}
        >
          {editingField ? (
            <FilterFieldEditor
              field={editingField}
              labels={labels}
              onDone={() => setIsSuggesting(false)}
              onBack={() => {
                setEditingKey(undefined)
                searchRef.current?.focus()
              }}
            />
          ) : (
            suggestions.map((suggestion, index) => (
              <div
                key={getSuggestionKey(suggestion)}
                role="option"
                aria-selected={index === activeIndex}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                  index === activeIndex && "bg-accent text-accent-foreground",
                )}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => onApplySuggestion(suggestion)}
              >
                {renderSuggestion(suggestion, labels)}
              </div>
            ))
          )}
        </PopoverContent>
      </Popover>
      {trailing}
    </div>
  )
}

type OptionsField = MultiSelectFilterBarField | SelectFilterBarField

type Suggestion =
  | { kind: "search"; text: string }
  | { kind: "field"; field: FilterBarField }
  | { kind: "value"; field: OptionsField; option: FilterBarOption }

function getSuggestions(
  fields: FilterBarField[],
  addableFields: FilterBarField[],
  draft: string,
): Suggestion[] {
  const text = normalize(draft).trim()
  if (!text) return addableFields.map((field) => ({ kind: "field", field }))

  const fieldSuggestions = sortByMatch(fields, (field) => field.label, text)
    .slice(0, MAX_FIELD_SUGGESTIONS)
    .map((field) => ({ kind: "field" as const, field }))

  const valueSuggestions = sortByMatch(
    fields.flatMap((field) => (field.type === "dateRange" ? [] : getValueSuggestions(field, text))),
    (suggestion) => suggestion.option.label,
    text,
  ).slice(0, MAX_VALUE_SUGGESTIONS)

  return [{ kind: "search", text: draft.trim() }, ...fieldSuggestions, ...valueSuggestions]
}

function findTypedField(fields: FilterBarField[], text: string) {
  const colonIndex = text.indexOf(":")
  if (colonIndex === -1) return undefined

  const label = normalize(text.slice(0, colonIndex)).trim()
  return fields.find((field) => normalize(field.label) === label)
}

function addTypedValues(field: FilterBarField, text: string) {
  if (field.type === "dateRange") return

  const values = text.split(",").flatMap((part) => findOption(field, part)?.value ?? [])
  if (values.length > 0) addValues(field, values)
}

function findOption(field: OptionsField, text: string) {
  const query = normalize(text).trim()
  if (!query) return undefined

  return sortByMatch(field.options, (option) => option.label, query).at(0)
}

function getValueSuggestions(field: OptionsField, query: string) {
  const selectedValues = getSelectedValues(field)
  const options = field.options.filter((option) => !selectedValues.includes(option.value))

  return sortByMatch(options, (option) => option.label, query).map((option) => ({
    kind: "value" as const,
    field,
    option,
  }))
}

function sortByMatch<T>(items: readonly T[], getLabel: (item: T) => string, query: string) {
  return items
    .map((item) => ({ item, rank: getMatchRank(normalize(getLabel(item)), query) }))
    .filter(({ rank }) => rank !== -1)
    .sort((a, b) => a.rank - b.rank)
    .map(({ item }) => item)
}

function getMatchRank(label: string, query: string) {
  if (label === query) return 0
  if (label.startsWith(query)) return 1
  if (label.split(/\s+/).some((word) => word.startsWith(query))) return 2
  if (label.includes(query)) return 3

  return -1
}

function getSuggestionKey(suggestion: Suggestion) {
  switch (suggestion.kind) {
    case "search":
      return "search"
    case "field":
      return `field:${suggestion.field.key}`
    case "value":
      return `value:${suggestion.field.key}:${suggestion.option.value}`
  }
}

function renderSuggestion(suggestion: Suggestion, labels: FilterBarLabels) {
  switch (suggestion.kind) {
    case "search":
      return (
        <>
          <Search className="h-4 w-4 text-muted-foreground" />
          <span className="truncate">{labels.searchFor(suggestion.text)}</span>
        </>
      )
    case "field":
      return (
        <>
          <ListFilter className="h-4 w-4 text-muted-foreground" />
          <span className="truncate">{suggestion.field.label}</span>
        </>
      )
    case "value":
      return (
        <>
          <Plus className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground shrink-0">{suggestion.field.label}:</span>
          <span className="truncate">{suggestion.option.label}</span>
          {suggestion.option.group && (
            <span className="ml-auto pl-2 shrink-0 text-xs text-muted-foreground">
              {suggestion.option.group}
            </span>
          )}
        </>
      )
  }
}

function toggleValue(field: MultiSelectFilterBarField, value: string) {
  const selectedValues = field.value ?? []
  const values = selectedValues.includes(value)
    ? selectedValues.filter((selectedValue) => selectedValue !== value)
    : [...selectedValues, value]

  field.onChange(values.length > 0 ? values : undefined)
}

function addValues(field: OptionsField, values: string[]) {
  if (field.type === "select") {
    field.onChange(values.at(-1))
    return
  }

  field.onChange([...new Set([...(field.value ?? []), ...values])])
}

function normalize(text: string) {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
}

function closeOnTab(event: KeyboardEvent<HTMLElement>, onOpenChange: (open: boolean) => void) {
  if (event.key !== "Tab") return

  event.preventDefault()
  onOpenChange(false)
}

function isCaretAtEdge(input: HTMLInputElement, key: "ArrowLeft" | "ArrowRight") {
  if (input.selectionStart !== input.selectionEnd) return false
  return key === "ArrowLeft"
    ? input.selectionStart === 0
    : input.selectionEnd === input.value.length
}

function scoreOption(value: string, search: string) {
  const rank = getMatchRank(normalize(value), normalize(search).trim())
  return rank === -1 ? 0 : 1 / (rank + 1)
}

function mergeKeyOrder(keyOrder: string[], activeKeys: string[]) {
  return [
    ...keyOrder.filter((key) => activeKeys.includes(key)),
    ...activeKeys.filter((key) => !keyOrder.includes(key)),
  ]
}

type InlinePillsProps = {
  fields: FilterBarField[]
  labels: FilterBarLabels
  boxRef: RefObject<HTMLDivElement | null>
  renderPill: (field: FilterBarField) => ReactNode
  onRemove: (field: FilterBarField) => void
}

function InlinePills({ fields, labels, boxRef, renderPill, onRemove }: InlinePillsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const overflowTriggerRef = useRef<HTMLButtonElement>(null)
  const [boxWidth, setBoxWidth] = useState(0)
  const [fittingCount, setFittingCount] = useState<number>()
  const [isOverflowOpen, setIsOverflowOpen] = useState(false)
  const hiddenKeysRef = useRef<string[]>([])

  const pillsKey = fields
    .map((field) => `${field.key}:${isFieldActive(field) ? getFieldSummary(field, labels) : ""}`)
    .join()

  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return

    const observer = new ResizeObserver(([entry]) => setBoxWidth(entry?.contentRect.width ?? 0))
    observer.observe(box)

    return () => observer.disconnect()
  }, [boxRef])

  useLayoutEffect(() => {
    if (!isOverflowOpen) setFittingCount(undefined)
  }, [pillsKey, boxWidth, isOverflowOpen])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || fittingCount !== undefined) return

    const pills = Array.from(container.children) as HTMLElement[]
    const fitsWithin = (width: number) =>
      pills.filter((pill) => pill.offsetLeft + pill.offsetWidth <= width).length

    const overflowTrigger = overflowTriggerRef.current
    const availableWidth = overflowTrigger
      ? overflowTrigger.getBoundingClientRect().right - container.getBoundingClientRect().left
      : container.clientWidth
    const isOverflowing = fitsWithin(availableWidth) < pills.length
    setFittingCount(fitsWithin(availableWidth - (isOverflowing ? OVERFLOW_PILL_WIDTH : 0)))
  }, [fittingCount, pillsKey])

  useLayoutEffect(() => {
    if (isNil(fittingCount)) return
    hiddenKeysRef.current = fields.slice(fittingCount).map((field) => field.key)
  })

  if (fields.length === 0) return null

  const shownFields = isNil(fittingCount) ? fields : fields.slice(0, fittingCount)
  const hiddenFields = isNil(fittingCount)
    ? fields.filter((field) => hiddenKeysRef.current.includes(field.key))
    : fields.slice(fittingCount)

  return (
    <>
      <div
        ref={containerRef}
        className="relative flex flex-nowrap items-center gap-1.5 min-w-0 overflow-hidden"
      >
        {shownFields.map(renderPill)}
      </div>
      {hiddenFields.length > 0 && (
        <OverflowMenu
          fields={fields}
          hiddenFields={hiddenFields}
          labels={labels}
          triggerRef={overflowTriggerRef}
          onRemove={onRemove}
          onOpenChange={setIsOverflowOpen}
        />
      )}
    </>
  )
}

type OverflowMenuProps = {
  fields: FilterBarField[]
  hiddenFields: FilterBarField[]
  labels: FilterBarLabels
  triggerRef: RefObject<HTMLButtonElement | null>
  onRemove: (field: FilterBarField) => void
  onOpenChange: (open: boolean) => void
}

function OverflowMenu(props: OverflowMenuProps) {
  const { fields, hiddenFields, labels, triggerRef } = props
  const [isOpen, setIsOpen] = useState(false)
  const [editingKey, setEditingKey] = useState<string>()

  const editingField = fields.find((field) => field.key === editingKey)
  const focusList = useCallback((list: HTMLDivElement | null) => list?.focus(), [])

  function onOpenChange(open: boolean) {
    setIsOpen(open)
    props.onOpenChange(open)
    if (open) setEditingKey(undefined)
  }

  function onRemove(field: FilterBarField) {
    props.onRemove(field)
    if (hiddenFields.length === 1) onOpenChange(false)
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className="flex items-center shrink-0 h-7 px-2.5 rounded-md bg-muted text-sm hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          +{hiddenFields.length}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-auto min-w-56"
        align="start"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onKeyDown={(event) => closeOnTab(event, onOpenChange)}
      >
        {editingField ? (
          <FilterFieldEditor
            field={editingField}
            labels={labels}
            onDone={() => onOpenChange(false)}
            onBack={() => setEditingKey(undefined)}
          />
        ) : (
          <Command
            ref={focusList}
            tabIndex={-1}
            className="outline-none"
            onKeyDown={(event) => {
              if (event.key !== "Delete" && event.key !== "Backspace") return

              const selected = event.currentTarget.querySelector<HTMLElement>(
                "[cmdk-item][aria-selected=true]",
              )
              const field = hiddenFields.find(
                (hidden) => hidden.key === selected?.dataset.filterKey,
              )
              if (!field) return

              event.preventDefault()
              onRemove(field)
            }}
          >
            <CommandList>
              <CommandGroup>
                {hiddenFields.map((field) => (
                  <CommandItem
                    key={field.key}
                    data-filter-key={field.key}
                    value={`${field.label} ${field.key}`}
                    onSelect={() => setEditingKey(field.key)}
                    className="gap-2"
                  >
                    <span className="shrink-0 text-muted-foreground">{field.label}</span>
                    <span className="max-w-48 truncate font-medium">
                      {getFieldSummary(field, labels)}
                    </span>
                    <span
                      role="button"
                      aria-label={labels.removeFilter}
                      className="ml-auto pl-2 text-muted-foreground hover:text-foreground"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation()
                        onRemove(field)
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}

type AddFilterButtonProps = {
  fields: FilterBarField[]
  addableFields: FilterBarField[]
  labels: FilterBarLabels
}

function AddFilterButton({ fields, addableFields, labels }: AddFilterButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [editingKey, setEditingKey] = useState<string>()

  const editingField = fields.find((field) => field.key === editingKey)

  if (addableFields.length === 0 && !isOpen) return null

  function onOpenChange(open: boolean) {
    setIsOpen(open)
    if (open) setEditingKey(undefined)
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <TooltipProvider>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7.5 w-7.5 shrink-0 rounded-sm"
                aria-label={labels.addFilter}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent>{labels.addFilter}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        className="p-0 w-auto min-w-56"
        align="center"
        onKeyDown={(event) => closeOnTab(event, onOpenChange)}
      >
        {editingField ? (
          <FilterFieldEditor
            field={editingField}
            labels={labels}
            onDone={() => onOpenChange(false)}
            onBack={() => setEditingKey(undefined)}
          />
        ) : (
          <Command>
            <CommandInput autoFocus placeholder={labels.search} />
            <CommandList>
              <CommandEmpty className="p-4 text-center text-sm text-muted-foreground">
                {labels.empty}
              </CommandEmpty>
              <CommandGroup>
                {addableFields.map((field) => (
                  <CommandItem
                    key={field.key}
                    value={`${field.label} ${field.key}`}
                    onSelect={() => setEditingKey(field.key)}
                  >
                    {field.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}

type FilterPillProps = {
  field: FilterBarField
  labels: FilterBarLabels
  isOpen: boolean
  isMarked: boolean
  onOpenChange: (open: boolean) => void
  onRemove: () => void
}

function FilterPill({ field, labels, isOpen, isMarked, onOpenChange, onRemove }: FilterPillProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const anchorRef = useRef<FrozenAnchor | null>(null)

  const isActive = isFieldActive(field)

  useLayoutEffect(() => {
    const trigger = triggerRef.current
    if (!isOpen || !trigger) return

    const { left } = trigger.getBoundingClientRect()

    anchorRef.current = {
      contextElement: trigger,
      getBoundingClientRect: () => {
        const { top, width, height } = trigger.getBoundingClientRect()
        return new DOMRect(left, top, width, height)
      },
    }
  }, [isOpen])

  return (
    <div
      data-filter-key={field.key}
      className={cn(
        "flex items-center shrink-0 h-7 rounded-md bg-muted text-sm",
        isMarked && "ring-2 ring-ring",
      )}
    >
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverAnchor virtualRef={anchorRef} />
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            className="flex items-center h-full max-w-80 rounded-l-md hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <span className="px-2.5 text-muted-foreground shrink-0">{field.label}</span>
            <span className="self-stretch my-1.5 w-px bg-foreground/10" />
            {isActive ? (
              <span className="pl-2.5 pr-1.5 font-medium truncate">
                {getFieldSummary(field, labels)}
              </span>
            ) : (
              <span className="pl-2.5 pr-1.5 text-muted-foreground">{labels.any}</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-auto min-w-56" align="start">
          <FilterFieldEditor field={field} labels={labels} onDone={() => onOpenChange(false)} />
          <div className="border-t p-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start font-normal"
              disabled={!isActive}
              onClick={() => {
                onRemove()
                onOpenChange(false)
              }}
            >
              {labels.clear}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label={labels.removeFilter}
        className="flex items-center h-full px-2 rounded-r-md text-muted-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        onClick={onRemove}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

type FilterFieldEditorProps = {
  field: FilterBarField
  labels: FilterBarLabels
  onDone: () => void
  onBack?: () => void
}

function FilterFieldEditor({ field, labels, onDone, onBack }: FilterFieldEditorProps) {
  switch (field.type) {
    case "multiSelect":
    case "select":
      return <OptionsEditor field={field} labels={labels} onDone={onDone} onBack={onBack} />
    case "dateRange":
      return (
        <div className="flex flex-col">
          {onBack && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="m-1 justify-start gap-1"
                onClick={onBack}
              >
                <ChevronLeft className="h-4 w-4" />
                {field.label}
              </Button>
              <div className="border-t" />
            </>
          )}
          <Calendar
            autoFocus
            mode="range"
            defaultMonth={field.value?.from}
            selected={field.value}
            numberOfMonths={2}
            onSelect={(range) => {
              if (!range?.from) {
                field.onChange(undefined)
                return
              }

              field.onChange({
                from: startOfDay(range.from),
                to: range.to ? endOfDay(range.to) : undefined,
              })

              if (range.to && !isSameDay(range.from, range.to)) onDone()
            }}
          />
        </div>
      )
  }
}

type OptionsEditorProps = {
  field: MultiSelectFilterBarField | SelectFilterBarField
  labels: FilterBarLabels
  onDone: () => void
  onBack?: () => void
}

function OptionsEditor({ field, labels, onDone, onBack }: OptionsEditorProps) {
  const selectedValues = getSelectedValues(field)

  function onSelect(value: string) {
    if (field.type === "select") {
      field.onChange(value)
      onDone()
      return
    }

    toggleValue(field, value)
  }

  return (
    <Command filter={scoreOption} shouldFilter={isNil(field.onOptionsSearchChange)}>
      <CommandInput
        autoFocus
        placeholder={labels.search}
        value={field.optionsSearch}
        onValueChange={field.onOptionsSearchChange}
        onKeyDown={(event) => {
          if (event.key === "Backspace" && event.currentTarget.value === "") onBack?.()
        }}
      />
      <CommandList className="max-h-80">
        <CommandEmpty className="p-4 text-center text-sm text-muted-foreground">
          {labels.empty}
        </CommandEmpty>
        {onBack && (
          <>
            <CommandGroup>
              <CommandItem value="←" onSelect={onBack} className="gap-1 font-medium">
                <ChevronLeft className="h-4 w-4" />
                {field.label}
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {groupOptions(field.options).map(([group, options]) => (
          <CommandGroup key={group ?? ""} heading={group}>
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value)

              return (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${group ?? ""} ${option.value}`}
                  onSelect={() => onSelect(option.value)}
                  className={cn("gap-2", option.className)}
                >
                  {field.type === "multiSelect" ? (
                    <Checkbox checked={isSelected} />
                  ) : (
                    <Check className={cn("h-4 w-4", !isSelected && "invisible")} />
                  )}
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  )
}

function isFieldActive(field: FilterBarField) {
  switch (field.type) {
    case "multiSelect":
      return (field.value?.length ?? 0) > 0
    case "select":
      return !isNil(field.value)
    case "dateRange":
      return !isNil(field.value?.from)
  }
}

function getSelectedValues(field: MultiSelectFilterBarField | SelectFilterBarField) {
  if (field.type === "multiSelect") return field.value ?? []
  return isNil(field.value) ? [] : [field.value]
}

function getFieldSummary(field: FilterBarField, labels: FilterBarLabels) {
  if (field.type === "dateRange") {
    if (!field.value?.from) return ""
    if (!field.value.to || isSameDay(field.value.from, field.value.to)) {
      return formatDate(field.value.from)
    }

    return `${formatDate(field.value.from)} - ${formatDate(field.value.to)}`
  }

  const selectedValues = getSelectedValues(field)
  const selectedLabels = field.options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label)

  if (selectedLabels.length === 0 || selectedValues.length > MAX_LABELS_IN_SUMMARY) {
    return labels.selectedCount(selectedValues.length)
  }

  return selectedLabels.join(", ")
}

function groupOptions(options: readonly FilterBarOption[]) {
  const optionsByGroup = new Map<string | undefined, FilterBarOption[]>()

  for (const option of options) {
    optionsByGroup.set(option.group, [...(optionsByGroup.get(option.group) ?? []), option])
  }

  return [...optionsByGroup.entries()]
}
